import { useState } from "react";
import { Link } from "react-router-dom";
import {
  Zap,
  Lightbulb,
  Plug,
  AlertCircle,
  ShieldCheck,
  Settings,
  Calendar,
  Cpu,
  Wrench,
  Package,
  Clock,
  Cog,
  AlignVerticalJustifyCenter,
  Search,
} from "lucide-react";

const ServicesDisplay = () => {
  const services = [
    {
      id: 1,
      title: "Installations & Upgrades",
      slug: "installations-upgrades",
      icon: Zap,
      category: "Electrical",
    },
    {
      id: 2,
      title: "Lighting Solutions",
      slug: "lighting-solutions",
      icon: Lightbulb,
      category: "Electrical",
    },
    {
      id: 3,
      title: "Rewiring & Power Point Installation",
      slug: "rewiring-power-points",
      icon: Plug,
      category: "Electrical",
    },
    {
      id: 4,
      title: "Fault Finding & Emergency Repairs",
      slug: "fault-finding-emergency-repairs",
      icon: AlertCircle,
      category: "Electrical",
    },
    {
      id: 5,
      title: "Compliance Inspections & Safety Certification",
      slug: "compliance-inspections",
      icon: ShieldCheck,
      category: "Electrical",
    },
    {
      id: 6,
      title: "Instrumentation & Control Wiring",
      slug: "instrumentation-control-wiring",
      icon: Settings,
      category: "Electrical",
    },
    {
      id: 7,
      title: "Preventive & Scheduled Maintenance",
      slug: "preventive-maintenance",
      icon: Calendar,
      category: "Electrical",
    },
    {
      id: 8,
      title: "Industrial Machine Wiring & Troubleshooting",
      slug: "industrial-machine-wiring",
      icon: Cpu,
      category: "Electrical",
    },
    {
      id: 9,
      title: "Field Service Support",
      slug: "field-service-support",
      icon: Wrench,
      category: "Mechanical",
    },
    {
      id: 10,
      title: "Equipment & System Installations",
      slug: "equipment-installations",
      icon: Package,
      category: "Mechanical",
    },
    {
      id: 11,
      title: "Scheduled Equipment Servicing",
      slug: "scheduled-equipment-servicing",
      icon: Clock,
      category: "Mechanical",
    },
    {
      id: 12,
      title: "General Maintenance & Repairs",
      slug: "general-maintenance-repairs",
      icon: Cog,
      category: "Mechanical",
    },
    {
      id: 13,
      title: "Precision Alignment",
      slug: "precision-alignment",
      icon: AlignVerticalJustifyCenter,
      category: "Mechanical",
    },
    {
      id: 14,
      title: "Mechanical Fault Finding",
      slug: "mechanical-fault-finding",
      icon: Search,
      category: "Mechanical",
    },
  ];

  const [filter, setFilter] = useState("All");
  const categories = ["All", "Electrical", "Mechanical"];
  const filtered =
    filter === "All" ? services : services.filter((s) => s.category === filter);

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Serif+Display:ital@0;1&family=DM+Sans:opsz,wght@9..40,300;9..40,400;9..40,500&display=swap');

        .svc-root {
          font-family: 'DM Sans', sans-serif;
          background: #fafaf8;
          border-bottom: 1px solid #e8e8e4;
        }

        /* ── Header ── */
        .svc-header {
          padding: 7rem 7vw 4rem;
          display: flex;
          align-items: flex-end;
          justify-content: space-between;
          gap: 3rem;
          flex-wrap: wrap;
          border-bottom: 1px solid #e8e8e4;
        }

        .svc-header-left {}

        .svc-eyebrow {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          margin-bottom: 1.5rem;
        }

        .svc-eyebrow-line {
          width: 28px;
          height: 1px;
          background: #E78730;
          flex-shrink: 0;
        }

        .svc-eyebrow-text {
          font-size: 0.68rem;
          letter-spacing: 0.26em;
          text-transform: uppercase;
          color: #E78730;
          font-weight: 500;
        }

        .svc-h2 {
          font-family: 'DM Serif Display', serif;
          font-size: clamp(2.25rem, 4.5vw, 3.5rem);
          line-height: 1.05;
          color: #1a1a1a;
          margin: 0;
        }

        .svc-h2 em {
          color: #E78730;
          font-style: italic;
        }

        .svc-header-right {
          max-width: 42ch;
        }

        .svc-desc {
          font-size: 0.93rem;
          color: #888;
          line-height: 1.8;
          font-weight: 300;
          margin-bottom: 2rem;
        }

        /* ── Filter tabs ── */
        .svc-filters {
          display: flex;
          gap: 0;
          border: 1px solid #e8e8e4;
          width: fit-content;
        }

        .svc-filter-btn {
          background: none;
          border: none;
          border-right: 1px solid #e8e8e4;
          padding: 0.6rem 1.5rem;
          font-size: 0.7rem;
          letter-spacing: 0.16em;
          text-transform: uppercase;
          font-weight: 400;
          color: #aaa;
          cursor: pointer;
          font-family: 'DM Sans', sans-serif;
          transition: background 0.2s, color 0.2s;
        }

        .svc-filter-btn:last-child { border-right: none; }

        .svc-filter-btn.active {
          background: #1a1a1a;
          color: #fff;
        }

        .svc-filter-btn:not(.active):hover {
          background: #f4f1ec;
          color: #1a1a1a;
        }

        /* ── Grid ── */
        .svc-grid-wrap {
          padding: 0;
        }

        .svc-grid {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          border-left: 1px solid #e8e8e4;
          border-top: 1px solid #e8e8e4;
        }

        /* ── Card ── */
        .svc-card {
          display: flex;
          flex-direction: column;
          justify-content: space-between;
          padding: 2.5rem 2.25rem;
          border-right: 1px solid #e8e8e4;
          border-bottom: 1px solid #e8e8e4;
          text-decoration: none;
          background: #fafaf8;
          transition: background 0.25s;
          min-height: 180px;
          position: relative;
          overflow: hidden;
        }

        .svc-card::before {
          content: '';
          position: absolute;
          bottom: 0; left: 0;
          width: 100%;
          height: 2px;
          background: #E78730;
          transform: scaleX(0);
          transform-origin: left;
          transition: transform 0.3s ease;
        }

        .svc-card:hover {
          background: #f4f1ec;
        }

        .svc-card:hover::before {
          transform: scaleX(1);
        }

        .svc-card-top {
          display: flex;
          align-items: flex-start;
          justify-content: space-between;
          margin-bottom: 1.5rem;
        }

        .svc-card-icon {
          transition: transform 0.3s;
        }

        .svc-card:hover .svc-card-icon {
          transform: translateY(-2px);
        }

        .svc-card-cat {
          font-size: 0.6rem;
          letter-spacing: 0.16em;
          text-transform: uppercase;
          font-weight: 500;
          padding: 0.25rem 0.6rem;
          border: 1px solid currentColor;
          line-height: 1.4;
        }

        .svc-card-cat.electrical { color: #6b8cba; border-color: rgba(107,140,186,0.3); }
        .svc-card-cat.mechanical { color: #E78730; border-color: rgba(231,135,48,0.3); }

        .svc-card-title {
          font-size: 0.88rem;
          color: #1a1a1a;
          font-weight: 400;
          line-height: 1.5;
        }

        .svc-card-arrow {
          position: absolute;
          bottom: 1.5rem;
          right: 1.5rem;
          color: #ccc;
          opacity: 0;
          transform: translateX(-4px);
          transition: opacity 0.25s, transform 0.25s;
        }

        .svc-card:hover .svc-card-arrow {
          opacity: 1;
          transform: translateX(0);
        }

        /* ── Responsive ── */
        @media (max-width: 1024px) {
          .svc-grid { grid-template-columns: repeat(3, 1fr); }
        }

        @media (max-width: 768px) {
          .svc-header { padding: 5rem 7vw 3rem; flex-direction: column; align-items: flex-start; }
          .svc-grid { grid-template-columns: repeat(2, 1fr); }
        }

        @media (max-width: 480px) {
          .svc-grid { grid-template-columns: 1fr; }
        }
      `}</style>

      <section className="svc-root">
        {/* Header */}
        <div className="svc-header">
          <div className="svc-header-left">
            <div className="svc-eyebrow">
              <span className="svc-eyebrow-line" />
              <span className="svc-eyebrow-text">What We Do</span>
            </div>
            <h2 className="svc-h2">
              Our <em>Services</em>
            </h2>
          </div>

          <div className="svc-header-right">
            <p className="svc-desc">
              Professional electrical and mechanical services tailored to your
              needs.
            </p>
            <div className="svc-filters">
              {categories.map((cat) => (
                <button
                  key={cat}
                  onClick={() => setFilter(cat)}
                  className={`svc-filter-btn${filter === cat ? " active" : ""}`}
                >
                  {cat}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Grid */}
        <div className="svc-grid-wrap">
          <div className="svc-grid">
            {filtered.map((service) => {
              const Icon = service.icon;
              return (
                <Link
                  key={service.id}
                  to={`/our-services#${service.slug}`}
                  className="svc-card"
                >
                  <div className="svc-card-top">
                    <Icon
                      size={22}
                      strokeWidth={1.25}
                      className="svc-card-icon"
                      color={
                        service.category === "Electrical"
                          ? "#6b8cba"
                          : "#E78730"
                      }
                    />
                    <span
                      className={`svc-card-cat ${service.category.toLowerCase()}`}
                    >
                      {service.category}
                    </span>
                  </div>

                  <h3 className="svc-card-title">{service.title}</h3>

                  <div className="svc-card-arrow">
                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.5"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M13 7l5 5m0 0l-5 5m5-5H6"
                      />
                    </svg>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>
    </>
  );
};

export default ServicesDisplay;
